import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Music } from 'lucide-react';

// Playlist of Pixabay royalty-free tracks
const TRACKS = [
  { name: "Arena Theme", file: "/hype-criminal-trap-beat.mp3", artist: "Poradovskyi" },
  { name: "Matchup Groove", file: "/trap-hype-beat.mp3", artist: "DELOSound" },
  { name: "Battle Ready", file: "/hip-hop-basketball.mp3", artist: "Poradovskyi" },
  { name: "Mic Drop", file: "/aggressive-fire-flow.mp3", artist: "onesevenbeatxs" },
  { name: "Round Start", file: "/trap-basketball-stinger.mp3", artist: "original_soundtrack" },
];

const MusicPlayer = ({ isRecording = false }) => {
  const audioRef = useRef(null);
  const wasPlayingRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const track = TRACKS[currentTrack];

  // Auto-pause when recording starts
  useEffect(() => {
    if (!audioRef.current) return;
    
    if (isRecording && isPlaying) {
      wasPlayingRef.current = true;
      audioRef.current.pause();
      setIsPlaying(false);
    } else if (!isRecording && wasPlayingRef.current) {
      wasPlayingRef.current = false;
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  }, [isRecording, isPlaying]);

  // Handle track end - auto next
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      // Auto-advance to next track
      const nextTrack = (currentTrack + 1) % TRACKS.length;
      setCurrentTrack(nextTrack);
    };

    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
    };
  }, [currentTrack]);

  // Auto-play when track changes (if already playing)
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(console.error);
    }
  }, [currentTrack]);

  const togglePlay = useCallback(async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        setIsLoading(true);
        await audioRef.current.play();
        setIsPlaying(true);
        if (isMuted) {
          setIsMuted(false);
          audioRef.current.volume = 0.7;
        }
      } catch (err) {
        console.error('Play error:', err);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isPlaying, isMuted]);

  const skipTrack = useCallback(() => {
    const nextTrack = (currentTrack + 1) % TRACKS.length;
    setCurrentTrack(nextTrack);
  }, [currentTrack]);

  const prevTrack = useCallback(() => {
    const prev = currentTrack === 0 ? TRACKS.length - 1 : currentTrack - 1;
    setCurrentTrack(prev);
  }, [currentTrack]);

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audioRef.current.volume = newMuted ? 0 : 0.7;
  }, [isMuted]);

  return (
    <div 
      className={`fixed bottom-16 left-4 z-50 transition-all duration-300 ${
        isExpanded ? 'w-72' : 'w-auto'
      }`}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={track.file}
        preload="auto"
      />

      <div className="bg-gradient-to-r from-red-600/90 to-blue-600/90 backdrop-blur-lg rounded-full shadow-lg border border-white/20">
        {isExpanded ? (
          /* Expanded View */
          <div className="flex items-center gap-2 px-3 py-2">
            {/* Track Info */}
            <div 
              className="flex-1 min-w-0 cursor-pointer"
              onClick={() => setIsExpanded(false)}
            >
              <p className="text-white text-sm font-bold truncate">{track.name}</p>
              <p className="text-white/60 text-xs truncate">{track.artist}</p>
            </div>

            {/* Visualizer animation */}
            {isPlaying && !isLoading && (
              <div className="flex items-end gap-0.5 h-4 mx-1">
                <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '8px', animationDelay: '0ms' }} />
                <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '16px', animationDelay: '150ms' }} />
                <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '12px', animationDelay: '300ms' }} />
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center gap-0.5">
              <button
                onClick={toggleMute}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 text-white/70" />
                ) : (
                  <Volume2 className="w-4 h-4 text-white" />
                )}
              </button>

              <button
                onClick={prevTrack}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              >
                <SkipBack className="w-4 h-4 text-white" />
              </button>
              
              <button
                onClick={togglePlay}
                disabled={isLoading}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors disabled:opacity-50"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-white" fill="white" />
                ) : (
                  <Play className="w-5 h-5 text-white" fill="white" />
                )}
              </button>

              <button
                onClick={skipTrack}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              >
                <SkipForward className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        ) : (
          /* Collapsed View */
          <button
            onClick={() => setIsExpanded(true)}
            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <Music className={`w-5 h-5 text-white ${isPlaying ? 'animate-pulse' : ''}`} />
            {isPlaying && (
              <div className="flex items-center gap-0.5">
                <div className="w-1 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1 h-4 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default MusicPlayer;
